import { useMemo, useState } from "react";
import { Dropdown, Button, Switch } from "antd";
import { EditOutlined, MoreOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import SearchBar from "../../components/searchBar";
import StatusTabs from "../../components/statusTabs";
import AddButton from "../../components/addButton";
import CommonTable from "../../components/commonTable";
import CustomPagination from "../../components/pagination";
import AddVendorModal from "../../components/modals/AddVendorModal";
import { useGetVendorsQuery, useToggleVendorStatusMutation } from "../../services/vendor";
import { VendorTransactionsRoute } from "../../routes/routepath";
import usePermissions from "../../hooks/usePermissions";
import "./styles.scss";

/* ─── Types ──────────────────────────────────────────────────────────── */

interface VendorRecord {
  key:              string;
  name?:            string;
  mobile?:          string;
  category?:        string;
  isActive?:        boolean;
  totalBillAmount?: number;
  totalPaid?:       number;
  totalDue?:        number;
}

/* ─── Component ──────────────────────────────────────────────────────── */

const AllVendors = () => {
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const [page,        setPage]        = useState(1);
  const [limit,       setLimit]       = useState(10);
  const [searchText,  setSearchText]  = useState("");
  const [activeTab,   setActiveTab]   = useState("all");
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [editVendor, setEditVendor] = useState<VendorRecord | null>(null);
  const [toggleVendorStatus, { isLoading: toggling }] = useToggleVendorStatusMutation();
  const [togglingId, setTogglingId] = useState<string | null>(null);

  const { data: vendorsResp, isFetching: vendorsLoading } = useGetVendorsQuery({
    search: searchText || undefined,
    status: activeTab as 'all' | 'active' | 'inactive',
    page,
    limit,
  });

  const apiData = vendorsResp as any;
  const counts  = apiData?.counts || {};

  const vendors = useMemo<VendorRecord[]>(() => {
    const rows: any[] = apiData?.data || [];
    return rows.map((v: any) => ({
      key:             v._id,
      name:            v.name,
      mobile:          v.mobile,
      category:        v.category,
      isActive:        v.isActive,
      totalBillAmount: v.totalBillAmount,
      totalPaid:       v.totalPaid,
      totalDue:        v.totalDue,
    }));
  }, [apiData]);

  const canToggleStatus = hasPermission('VENDOR_STATUS_TOGGLE');

  const handleToggleStatus = async (record: VendorRecord, checked: boolean) => {
    if (!canToggleStatus) return;
    setTogglingId(record.key);
    try {
      await toggleVendorStatus({ id: record.key, isActive: checked }).unwrap();
    } catch {
      /* error toast handled globally by badRequestHandler */
    } finally {
      setTogglingId(null);
    }
  };

  const tabsData = [
    { key: 'all',      label: 'All',      count: counts.all      || 0 },
    { key: 'active',   label: 'Active',   count: counts.active   || 0 },
    { key: 'inactive', label: 'Inactive', count: counts.inactive || 0 },
  ];

  const columns = [
    { title: 'S.No',   key: 'sno', width: 60, render: (_: unknown, __: unknown, i: number) => (page - 1) * limit + i + 1 },
    { title: 'Name',   dataIndex: 'name',   key: 'name',
      render: (name: string, record: VendorRecord) => (
        <span
          style={{ color: '#1890ff', cursor: 'pointer', textDecoration: 'underline' }}
          onClick={() => navigate(`${VendorTransactionsRoute}?vendorId=${record.key}`, { state: { vendorName: name } })}
        >
          {name}
        </span>
      ) },
    { title: 'Mobile', dataIndex: 'mobile', key: 'mobile' },
    { title: 'Category', dataIndex: 'category', key: 'category', render: (v?: string) => v || '-' },
    { title: 'Bill Amount', dataIndex: 'totalBillAmount', key: 'totalBillAmount',
      render: (v?: number) => v !== undefined ? `₹${v.toLocaleString('en-IN')}` : '-' },
    { title: 'Paid Amount', dataIndex: 'totalPaid', key: 'totalPaid',
      render: (v?: number) => v !== undefined ? <span style={{ color: '#52c41a' }}>₹{v.toLocaleString('en-IN')}</span> : '-' },
    { title: 'Pending Amount', dataIndex: 'totalDue', key: 'totalDue',
      render: (v?: number) => v !== undefined ? <span style={{ color: v > 0 ? '#ff4d4f' : undefined }}>₹{v.toLocaleString('en-IN')}</span> : '-' },
    { title: 'Status', dataIndex: 'isActive', key: 'isActive', align: 'center' as const,
      render: (isActive: boolean, record: VendorRecord) => (
        <Switch
          checked={isActive}
          disabled={!canToggleStatus}
          loading={toggling && togglingId === record.key}
          onChange={(checked) => handleToggleStatus(record, checked)}
        />
      ) },
    ...(hasPermission('VENDOR_UPDATE') ? [{
      title: 'Actions', key: 'actions', align: 'center' as const,
      render: (_: unknown, record: VendorRecord) => (
        <Dropdown
          trigger={['click']}
          placement="bottomRight"
          menu={{
            items: [
              { key: 'edit', label: 'Edit', icon: <EditOutlined />, onClick: () => setEditVendor(record) },
            ],
          }}
        >
          <Button type="text" icon={<MoreOutlined style={{ fontSize: 20 }} />} />
        </Dropdown>
      ),
    }] : []),
  ];

  return (
    <div className="all-vendors-page">
      <div className="header-section">
        <div className="left-col">
          <SearchBar
            value={searchText}
            onChange={(v: string) => { setSearchText(v); setPage(1); }}
            placeholder="Search vendors..."
          />
          <StatusTabs
            activeTab={activeTab}
            onTabChange={(tab: string) => { setActiveTab(tab); setPage(1); }}
            tabs={tabsData}
          />
        </div>
        <div className="right-col">
          {hasPermission('VENDOR_CREATE') && (
            <AddButton onClick={() => setAddModalOpen(true)}>Add Vendor</AddButton>
          )}
        </div>
      </div>

      <div className="vendors-table-wrapper">
        <CommonTable
          columns={columns}
          dataSource={vendors}
          loading={vendorsLoading}
          pagination={false}
          rowKey={(record: VendorRecord) => record.key}
        />
      </div>

      <CustomPagination
        current={page}
        pageSize={limit}
        total={apiData?.total || 0}
        onPageChange={(newPage: number) => setPage(newPage)}
        onPageSizeChange={(newLimit: number) => { setLimit(newLimit); setPage(1); }}
      />

      <AddVendorModal
        visible={addModalOpen || !!editVendor}
        vendor={editVendor ? { _id: editVendor.key, name: editVendor.name, mobile: editVendor.mobile, category: editVendor.category } : null}
        onCancel={() => { setAddModalOpen(false); setEditVendor(null); }}
      />
    </div>
  );
};

export default AllVendors;
